import {
  AlertCircle,
  Banknote,
  Boxes,
  PackageCheck,
  ShoppingCart,
} from "lucide-react";
import { useEffect, useState } from "react";
import { StatCard } from "../components/StatCard";
import { api } from "../lib/api";
import { formatCurrency, statusTone } from "../lib/format";
import type { Order, StatsResponse, SummaryRow } from "../types";

export function Dashboard() {
  const [stats, setStats] = useState<StatsResponse | null>(null);
  const [error, setError] = useState("");

  useEffect(() => {
    api
      .stats()
      .then(setStats)
      .catch((err) => setError(err.message));
  }, []);

  if (error) {
    return <div className="notice danger">{error}</div>;
  }

  if (!stats) {
    return <div className="notice">加载统计中...</div>;
  }

  return (
    <section className="page-stack">
      <header className="page-header">
        <div>
          <p className="eyebrow">Dashboard</p>
          <h1>订单统计</h1>
        </div>
      </header>

      <div className="stats-grid">
        <StatCard
          icon={ShoppingCart}
          label="总订单数"
          value={`${stats.totals.orderCount}`}
          subtext="按平台、大类、日期排序"
        />
        <StatCard
          icon={Banknote}
          label="总金额"
          value={formatCurrency(stats.totals.totalAmount)}
          subtext="D1 实时汇总"
        />
        <StatCard
          icon={PackageCheck}
          label="待物流"
          value={`${stats.pendingLogistics.length}`}
          subtext="暂无物流或未发货"
        />
        <StatCard
          icon={Boxes}
          label="商品数"
          value={`${stats.productSummary.length}`}
          subtext="按识别后的商品名"
        />
      </div>

      <div className="summary-grid">
        <SummaryCard title="平台汇总" rows={stats.platformSummary} />
        <SummaryCard title="大类汇总" rows={stats.categorySummary} />
        <SummaryCard title="商品汇总" rows={stats.productSummary} />
        <SummaryCard title="付款方式汇总" rows={stats.paymentSummary} />
        <SummaryCard title="物流状态汇总" rows={stats.shippingSummary} />
      </div>

      <section className="panel">
        <div className="panel-title">
          <AlertCircle size={18} aria-hidden />
          <h2>暂无物流或未发货订单</h2>
        </div>
        <PendingTable orders={stats.pendingLogistics} />
      </section>
    </section>
  );
}

function SummaryCard({ title, rows }: { title: string; rows: SummaryRow[] }) {
  return (
    <section className="panel">
      <div className="panel-title">
        <h2>{title}</h2>
      </div>
      <div className="summary-table">
        {rows.length === 0 ? (
          <p className="empty">暂无数据</p>
        ) : (
          rows.map((row) => (
            <div className="summary-row" key={row.name}>
              <span>{row.name}</span>
              <strong>{row.count}</strong>
              <em>{formatCurrency(row.amount)}</em>
            </div>
          ))
        )}
      </div>
    </section>
  );
}

function PendingTable({ orders }: { orders: Order[] }) {
  if (orders.length === 0) {
    return <p className="empty">暂无待物流订单</p>;
  }

  return (
    <div className="table-wrap">
      <table>
        <thead>
          <tr>
            <th>平台</th>
            <th>大类</th>
            <th>日期</th>
            <th>客户</th>
            <th>商品</th>
            <th>金额</th>
            <th>物流</th>
          </tr>
        </thead>
        <tbody>
          {orders.map((order) => (
            <tr key={order.id}>
              <td>{order.platform}</td>
              <td>{order.category}</td>
              <td>{order.order_date}</td>
              <td>{order.customer_name || "-"}</td>
              <td>{order.product_name}</td>
              <td>{formatCurrency(order.amount)}</td>
              <td>
                <span className={`badge ${statusTone(order.shipping_status)}`}>
                  {order.shipping_status || "未填写"}
                </span>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}
